import { yupResolver } from '@hookform/resolvers/yup';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
	Alert,
	Box,
	Button,
	Card,
	CardActions,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import {
	LoginAccountEntity,
	LoginAccountTarget,
	LoginIdCheckHitDTO,
} from '@/api/DTO/login-account/loginAccount.interface';
import {
	useCheckLoginIdMutation,
	useGetLoginAccountQuery,
	useUpdateLoginIdMutation,
} from '@/api/login-account/loginAccountApi';
import LoginIdHitsTable from './LoginIdHitsTable';
import { programAppLoginTabPath } from './ProgramAppLoginTab';

interface Props {
	entity: LoginAccountEntity;
}

const LOGIN_ID_MIN_LENGTH = 6;
const LOGIN_ID_MAX_LENGTH = 20;

export default function EditLoginIdPage(props: Props) {
	const { entity } = props;
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { admissionRequestId, familyId } = useParams();
	const id = (entity === 'member' ? admissionRequestId : familyId) || '';
	const target: LoginAccountTarget = { entity, id };

	const { data: accountResponse } = useGetLoginAccountQuery(target, {
		skip: !id,
		refetchOnMountOrArgChange: true,
	});
	const account = accountResponse?.data;
	const currentLoginId = account?.loginId ?? null;

	const [checkLoginId] = useCheckLoginIdMutation();
	const [updateLoginId] = useUpdateLoginIdMutation();
	const [taken, setTaken] = useState<{
		loginId: string;
		hits: LoginIdCheckHitDTO[];
	} | null>(null);

	const formSchema = Yup.object().shape({
		loginId: Yup.string()
			.trim()
			.required(t('loginAccount.loginIdRequired'))
			.matches(/^[A-Za-z0-9]+$/, t('loginAccount.loginIdInvalidFormat'))
			.min(LOGIN_ID_MIN_LENGTH, t('loginAccount.loginIdInvalidLength'))
			.max(LOGIN_ID_MAX_LENGTH, t('loginAccount.loginIdInvalidLength')),
	});

	const methods = useForm<{ loginId: string }>({
		defaultValues: { loginId: '' },
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		if (account) {
			methods.reset({ loginId: account.loginId ?? '' });
		}
	}, [account]);

	const handleClose = () => {
		navigate(programAppLoginTabPath(entity, id), { replace: true });
	};

	const onSubmit: SubmitHandler<{ loginId: string }> = async (data) => {
		const loginId = data.loginId.trim();
		if (currentLoginId && loginId === currentLoginId) {
			handleClose();
			return;
		}

		setTaken(null);
		const checkResponse = await checkLoginId({ ...target, loginId });
		if (!('data' in checkResponse) || !checkResponse.data) return;
		if (!checkResponse.data.data.loginIdAvailable) {
			setTaken({ loginId, hits: checkResponse.data.data.hits });
			methods.setError('loginId', { message: t('loginAccount.loginIdTaken') });
			return;
		}

		const response = await updateLoginId({ ...target, loginId });
		if ('data' in response) {
			enqueueSnackbar(t('loginAccount.updateLoginIdSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	return (
		<Box sx={{ width: '100%' }}>
			<form onSubmit={methods.handleSubmit(onSubmit)}>
				<Typography variant='h4'>
					{entity === 'member'
						? t('drawerListItems.editMember')
						: t('drawerListItems.editFamilyMember')}
				</Typography>
				<Card sx={{ padding: '24px', marginTop: 3 }}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('loginAccount.loginAccount')}
					</Typography>
					{account?.sharedWithKat && (
						<Alert severity='warning' sx={{ marginBottom: 2 }}>
							{t('loginAccount.sharedWithKat')}
						</Alert>
					)}
					<Grid container spacing={3} marginTop={0} marginBottom={3}>
						<Grid size={6}>
							<AppTextField
								control={methods.control}
								name='loginId'
								label={t('loginAccount.loginId') + '*'}
								maxLength={LOGIN_ID_MAX_LENGTH}
							/>
						</Grid>
						{taken && (
							<Grid size={12}>
								<Alert
									severity='warning'
									sx={{ '& .MuiAlert-message': { width: '100%' } }}
								>
									<Stack spacing={2}>
										<Typography variant='body2'>
											{t('loginAccount.loginIdTakenBy', {
												loginId: taken.loginId,
											})}
										</Typography>
										<LoginIdHitsTable hits={taken.hits} />
									</Stack>
								</Alert>
							</Grid>
						)}
					</Grid>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							endIcon={<CheckCircleOutlineIcon />}
						>
							{t('button.complete')}
						</Button>
					</CardActions>
				</Card>
			</form>
		</Box>
	);
}
